import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatIDR, parseIDR } from "@/lib/format";
import { Card, PageHeader, Button, Field, Input, Modal, Textarea, Badge } from "@/components/ui-kit";
import { useTableFilter } from "@/hooks/useTableFilter";
import { usePagination } from "@/hooks/usePagination";
import { ShieldCheck, Pencil, AlertTriangle, Infinity as InfinityIcon, Send, Stethoscope, Check, X as XIcon } from "lucide-react";

/**
 * Iter v34cq (#7) — Plafon Kredit Klien.
 * Menampilkan tagihan belum jatuh tempo, tagihan jatuh tempo, dan batas plafon
 * yang dapat diedit per klien (atau tanpa plafon / unlimited).
 */
export default function CreditLimits() {
  const [rows, setRows] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [formLimit, setFormLimit] = useState({ unlimited: true, credit_limit: "", note: "", notify_email: "" });
  const [saving, setSaving] = useState(false);
  const [alertCfg, setAlertCfg] = useState(null);
  const [notifying, setNotifying] = useState("");
  // Iter v34ct (#1) — Diagnosa kenapa notifikasi plafon tidak terkirim.
  const [diag, setDiag] = useState(null);
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagLoading, setDiagLoading] = useState(false);

  const openDiag = async () => {
    setDiagOpen(true); setDiagLoading(true);
    try {
      const r = await api.get("/credit-limits/notify-diagnostics");
      setDiag(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat diagnosa");
    } finally { setDiagLoading(false); }
  };

  const { filtered, FilterBar } = useTableFilter(rows, ["client_name"], "Cari nama klien…");
  const { pageItems, Pagination } = usePagination(filtered, { perPage: 25 });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/credit-limits");
      setRows(r.data?.rows || []);
      setCanEdit(!!r.data?.can_edit);
      setAlertCfg(r.data?.alert || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat data plafon");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openEdit = (r) => {
    setActive(r);
    setFormLimit({
      unlimited: !!r.unlimited,
      credit_limit: r.unlimited ? "" : String(r.credit_limit ?? ""),
      note: r.note || "",
      notify_email: r.notify_email || "",
    });
  };

  // Iter v34cr — Kirim notifikasi plafon manual (semua klien atau 1 klien).
  const notify = async (clientName = null) => {
    setNotifying(clientName || "ALL");
    try {
      const r = await api.post("/credit-limits/notify", null, {
        params: clientName ? { client_name: clientName } : {},
      });
      const d = r.data || {};
      if (d.skipped) { toast.error(d.skipped); return; }
      if (!d.eligible) { toast.error("Tidak ada klien yang mencapai ambang peringatan plafon"); return; }
      const lines = [];
      if (d.marketing) lines.push(d.marketing.ok ? `✓ Marketing → ${(d.marketing.recipients || []).join(", ")}` : `✗ Marketing: ${d.marketing.error}`);
      (d.clients || []).forEach((c) => lines.push(c.ok ? `✓ ${c.client_name} → ${c.to}` : `✗ ${c.client_name}: ${c.error}`));
      if (d.sent > 0) toast.success(`${d.sent} email terkirim`, { description: lines.join("\n"), duration: 9000 });
      else toast.error("Tidak ada email terkirim", { description: lines.join("\n"), duration: 9000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim notifikasi");
    } finally {
      setNotifying("");
    }
  };

  const save = async () => {
    if (!active) return;
    setSaving(true);
    try {
      await api.put("/credit-limits", {
        client_name: active.client_name,
        client_id: active.client_id || "",
        unlimited: !!formLimit.unlimited,
        credit_limit: formLimit.unlimited ? null : parseIDR(formLimit.credit_limit),
        note: formLimit.note || "",
        notify_email: (formLimit.notify_email || "").trim(),
      });
      toast.success(`Plafon ${active.client_name} tersimpan`);
      setActive(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan plafon");
    } finally {
      setSaving(false);
    }
  };

  const totalOverdue = rows.reduce((s, r) => s + (r.overdue_amount || 0), 0);
  const totalNotDue = rows.reduce((s, r) => s + (r.not_due_amount || 0), 0);
  const overCount = rows.filter((r) => r.over_limit).length;
  const withLimit = rows.filter((r) => !r.unlimited).length;

  return (
    <div className="min-h-full bg-[#f6f5f0]">
      <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
        <PageHeader
          overline="Keuangan · Kontrol Kredit"
          title="Plafon Kredit Klien"
          description="Atur batas plafon tiap klien. Jika tagihan yang sudah jatuh tempo melebihi plafon, pembuatan Surat Jalan baru untuk klien tersebut akan diblokir sampai ada pembayaran."
        />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-2xl bg-white border border-stone-200/70 p-4">
            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Belum Jatuh Tempo</div>
            <div className="font-mono-num text-xl mt-1 text-stone-900" data-testid="cl-kpi-not-due">{formatIDR(totalNotDue)}</div>
          </div>
          <div className="rounded-2xl bg-amber-50/60 border border-amber-100 p-4">
            <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Sudah Jatuh Tempo</div>
            <div className="font-mono-num text-xl mt-1 text-amber-800" data-testid="cl-kpi-overdue">{formatIDR(totalOverdue)}</div>
          </div>
          <div className="rounded-2xl bg-white border border-stone-200/70 p-4">
            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Klien Berplafon</div>
            <div className="font-display text-3xl mt-1 text-stone-900">{withLimit}</div>
          </div>
          <div className="rounded-2xl bg-red-50/60 border border-red-100 p-4">
            <div className="text-[10px] uppercase tracking-wider text-red-700 font-semibold">Melebihi Plafon</div>
            <div className="font-display text-3xl mt-1 text-red-800" data-testid="cl-kpi-over">{overCount}</div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          {FilterBar}
          <div className="flex items-center gap-3 flex-wrap">
            {alertCfg && (
              <span className="text-xs text-stone-500" data-testid="cl-alert-config">
                Notifikasi: <b className={alertCfg.enabled ? "text-emerald-700" : "text-stone-400"}>{alertCfg.enabled ? "aktif" : "nonaktif"}</b>
                {" "}· ambang {Number(alertCfg.threshold_pct).toFixed(0)}% · {alertCfg.time} WIB
              </span>
            )}
            {canEdit && (
              <Button variant="ghost" onClick={openDiag} data-testid="cl-diagnose-btn">
                <Stethoscope className="h-3.5 w-3.5" /> Diagnosa Notifikasi
              </Button>
            )}
            {canEdit && (
              <Button variant="secondary" onClick={() => notify(null)} disabled={!!notifying} data-testid="cl-notify-all-btn">
                <Send className="h-3.5 w-3.5" />{notifying === "ALL" ? "Mengirim…" : "Kirim Notifikasi Plafon"}
              </Button>
            )}
            <span className="text-xs text-stone-500 inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" />
              {canEdit ? "Anda dapat mengedit plafon" : "Mode lihat saja (tanpa hak edit)"}
            </span>
          </div>
        </div>

        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
                <th className="py-3 px-3">Nama Klien</th>
                <th className="py-3 px-3 text-right">Tagihan Belum Jatuh Tempo</th>
                <th className="py-3 px-3 text-right">Tagihan Jatuh Tempo</th>
                <th className="py-3 px-3 text-right">Batas Plafon</th>
                <th className="py-3 px-3 text-right">Pemakaian</th>
                <th className="py-3 px-3 text-center">Status</th>
                <th className="py-3 px-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="py-12 text-center text-stone-400">Memuat…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="py-12 text-center text-stone-400">Belum ada data klien.</td></tr>
              )}
              {!loading && pageItems.map((r, idx) => (
                <tr key={r.client_name} className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`cl-row-${idx}`}>
                  <td className="py-2.5 px-3 font-medium">{r.client_name}</td>
                  <td className="py-2.5 px-3 text-right font-mono-num text-stone-700" data-testid={`cl-not-due-${idx}`}>
                    {formatIDR(r.not_due_amount)}
                    {r.not_due_count > 0 && <span className="text-stone-400 text-[10px] ml-1">({r.not_due_count} inv)</span>}
                  </td>
                  <td className={`py-2.5 px-3 text-right font-mono-num ${r.overdue_amount > 0 ? "text-amber-800 font-semibold" : "text-stone-500"}`} data-testid={`cl-overdue-${idx}`}>
                    {formatIDR(r.overdue_amount)}
                    {r.overdue_count > 0 && <span className="text-stone-400 text-[10px] ml-1">({r.overdue_count} inv)</span>}
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono-num" data-testid={`cl-limit-${idx}`}>
                    {r.unlimited
                      ? <span className="inline-flex items-center gap-1 text-stone-500"><InfinityIcon className="h-3.5 w-3.5" />Tanpa Plafon</span>
                      : formatIDR(r.credit_limit)}
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono-num" data-testid={`cl-usage-${idx}`}>
                    {r.unlimited ? <span className="text-stone-300">—</span> : (
                      <span className={r.usage_pct >= 100 ? "text-red-700 font-bold" : r.usage_pct >= 80 ? "text-amber-700 font-semibold" : "text-stone-600"}>
                        {Number(r.usage_pct || 0).toFixed(0)}%
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    {r.over_limit
                      ? <Badge tone="danger" className="gap-1"><AlertTriangle className="h-3 w-3" /> Melebihi Plafon</Badge>
                      : r.unlimited
                        ? <span className="text-[10px] uppercase tracking-wider text-stone-400">—</span>
                        : <Badge tone="success">Aman</Badge>}
                  </td>
                  <td className="py-2.5 px-3 text-right whitespace-nowrap">
                    {canEdit && !r.unlimited && (r.usage_pct || 0) > 0 && (
                      <button onClick={() => notify(r.client_name)} disabled={!!notifying} className="text-stone-500 hover:text-terra-700 px-2" title="Kirim notifikasi plafon ke marketing & klien" data-testid={`cl-notify-${idx}`}>
                        <Send className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {canEdit && (
                      <button onClick={() => openEdit(r)} className="text-stone-500 hover:text-terra-700 px-2" title="Edit plafon" data-testid={`cl-edit-${idx}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        {Pagination}

        <Modal open={!!active} onClose={() => setActive(null)} title={active ? `Plafon Kredit · ${active.client_name}` : ""} size="md">
          {active && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 bg-stone-50 border border-stone-200 p-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">Belum Jatuh Tempo</div>
                  <div className="font-mono-num">{formatIDR(active.not_due_amount)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">Sudah Jatuh Tempo</div>
                  <div className="font-mono-num text-amber-800 font-semibold">{formatIDR(active.overdue_amount)}</div>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!formLimit.unlimited}
                  onChange={(e) => setFormLimit({ ...formLimit, unlimited: e.target.checked })}
                  data-testid="cl-unlimited-toggle" />
                Tanpa plafon (tidak dibatasi)
              </label>

              <Field label="Batas Plafon (Rp)" hint="Blokir SJ berlaku bila tagihan jatuh tempo melebihi nilai ini">
                <Input value={formLimit.credit_limit} disabled={!!formLimit.unlimited}
                  onChange={(e) => setFormLimit({ ...formLimit, credit_limit: e.target.value })}
                  placeholder="0" className="font-mono-num text-right" data-testid="cl-limit-input" />
              </Field>

              <Field label="Catatan (opsional)">
                <Textarea rows={2} value={formLimit.note} onChange={(e) => setFormLimit({ ...formLimit, note: e.target.value })} data-testid="cl-note-input" />
              </Field>

              <Field label="Email Notifikasi Klien (override)" hint="Kosongkan untuk memakai email akun portal klien / master Klien">
                <Input value={formLimit.notify_email} onChange={(e) => setFormLimit({ ...formLimit, notify_email: e.target.value })} placeholder="finance@klien.co.id" data-testid="cl-notify-email-input" />
              </Field>

              <div className="flex justify-end gap-2 pt-3 border-t border-stone-200">
                <Button variant="ghost" onClick={() => setActive(null)}>Batal</Button>
                <Button onClick={save} disabled={saving} data-testid="cl-save-btn">{saving ? "Menyimpan…" : "Simpan"}</Button>
              </div>
            </div>
          )}
        </Modal>

        {/* Iter v34ct (#1) — Panel diagnosa notifikasi plafon */}
        <Modal open={diagOpen} onClose={() => setDiagOpen(false)} title="Diagnosa Notifikasi Plafon" size="lg">
          <div className="space-y-4">
            {diagLoading && <p className="py-8 text-center text-stone-400">Memeriksa…</p>}
            {!diagLoading && diag && (
              <>
                <div className="border border-stone-200 divide-y divide-stone-100" data-testid="cl-diag-checks">
                  {(diag.checks || []).map((c) => (
                    <div key={c.key} className="p-3 flex items-start gap-3" data-testid={`cl-diag-${c.key}`}>
                      <span className={`mt-0.5 h-5 w-5 rounded-full flex items-center justify-center shrink-0 ${c.ok ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        {c.ok ? <Check className="h-3 w-3" /> : <XIcon className="h-3 w-3" />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-stone-900">{c.label}</p>
                        <p className="text-xs text-stone-600 break-words">{c.detail}</p>
                        {!c.ok && <p className="text-[11px] text-amber-800 mt-1">→ {c.hint}</p>}
                      </div>
                    </div>
                  ))}
                </div>

                {(diag.invalid_manual_recipients || []).length > 0 && (
                  <div className="bg-red-50 border border-red-200 p-3 text-xs text-red-800">
                    Email penerima tidak valid dan diabaikan: <b>{diag.invalid_manual_recipients.join(", ")}</b>
                  </div>
                )}

                <div>
                  <p className="text-[11px] uppercase tracking-widest font-semibold text-stone-600 mb-1">Email Klien Terdeteksi</p>
                  <div className="border border-stone-200 max-h-[200px] overflow-y-auto">
                    <table className="w-full text-xs">
                      <tbody>
                        {(diag.clients || []).length === 0 && (
                          <tr><td className="py-4 px-3 text-center text-stone-400">Belum ada klien yang mencapai ambang.</td></tr>
                        )}
                        {(diag.clients || []).map((c) => (
                          <tr key={c.client_name} className="border-b border-stone-100">
                            <td className="py-1.5 px-3 font-medium">{c.client_name}</td>
                            <td className="py-1.5 px-3 font-mono-num text-right">{Number(c.usage_pct || 0).toFixed(0)}%</td>
                            <td className="py-1.5 px-3">
                              {c.email
                                ? <span className={c.email_valid ? "text-emerald-700" : "text-red-700"}>{c.email}{!c.email_valid && " (tidak valid)"}</span>
                                : <span className="text-red-700">belum ada email</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t border-stone-100">
              <Button variant="ghost" onClick={() => setDiagOpen(false)}>Tutup</Button>
              <Button onClick={openDiag} disabled={diagLoading} data-testid="cl-diag-refresh">Periksa Ulang</Button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
}
